import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const workshopFeatures = [
  {
    id: '1',
    title: 'Recuperação de Cilindros Hidráulicos',
    desc: 'Recuperação completa de cilindros hidráulicos, incluindo diagnóstico técnico, desmontagem, recuperação de componentes críticos, montagem e teste em bancada para garantir confiabilidade operacional.',
    icon: 'ri-hammer-line',
  },
  {
    id: '2',
    title: 'Recuperação de Bombas Hidráulicas',
    desc: 'Overhaul técnico de bombas de pistão, engrenagem e palheta. Diagnóstico especializado, substituição de componentes críticos e validação em bancada de testes para devolução à operação.',
    icon: 'ri-settings-4-line',
  },
  {
    id: '3',
    title: 'Recuperação de Motores Hidráulicos',
    desc: 'Recuperação de motores hidráulicos axiais e radiais. Substituição de conjuntos rotativos, vedações e testes operacionais para máxima disponibilidade do equipamento.',
    icon: 'ri-refresh-line',
  },
  {
    id: '4',
    title: 'Recuperação de Redutores Industriais',
    desc: 'Recuperação técnica de redutores com diagnóstico especializado, alinhamento, substituição de peças críticas e testes operacionais para máxima performance industrial.',
    icon: 'ri-refresh-line',
  },
  {
    id: '5',
    title: 'Bancada de Testes',
    desc: 'Bancadas equipadas com instrumentos de precisão para teste de pressão, vazão, temperatura e funcionamento de componentes hidráulicos, garantindo validação antes do retorno à operação.',
    icon: 'ri-flask-line',
  },
  {
    id: '6',
    title: 'Engenharia e Diagnóstico Técnico',
    desc: 'Análise técnica especializada, diagnóstico de falhas, planejamento de recuperação e acompanhamento do processo completo por engenheiros e técnicos especialistas.',
    icon: 'ri-building-line',
  },
];

const capabilities = [
  {
    title: 'Diagnóstico Técnico',
    desc: 'Diagnóstico detalhado de falhas, avaliação de desgaste e análise de viabilidade de recuperação para cada equipamento recebido.',
    icon: 'ri-search-line',
  },
  {
    title: 'Desmontagem Técnica',
    desc: 'Desmontagem técnica de componentes hidráulicos e mecânicos com identificação, catalogação e avaliação de todas as peças.',
    icon: 'ri-tools-line',
  },
  {
    title: 'Recuperação de Equipamentos',
    desc: 'Recuperação completa de componentes e equipamentos, desde a avaliação inicial até o teste final de aprovação e devolução à operação.',
    icon: 'ri-recycle-line',
  },
  {
    title: 'Bancada de Testes',
    desc: 'Testes de pressão, vazão, temperatura e funcionamento em bancadas específicas para garantir a performance e a segurança do equipamento.',
    icon: 'ri-flask-line',
  },
  {
    title: 'Soldagem e Recuperação Estrutural',
    desc: 'Serviços de soldagem e recuperação estrutural de componentes e equipamentos, incluindo acompanhamento técnico de processos especializados.',
    icon: 'ri-fire-line',
  },
  {
    title: 'Gestão de Serviços Especializados',
    desc: 'Coordenação completa dos processos de recuperação, incluindo gestão de serviços especializados executados por parceiros homologados e acompanhados tecnicamente.',
    icon: 'ri-settings-5-line',
  },
];

const integratedServices = [
  'Retífica de Componentes',
  'Recuperação de Hastes',
  'Cromagem Industrial',
  'Brunimento',
  'Usinagens Especiais',
  'Tratamentos Superficiais',
];

const equipmentCategories = [
  {
    title: 'Hidráulicos',
    items: [
      'Cilindros Hidráulicos',
      'Bombas Hidráulicas',
      'Motores Hidráulicos',
      'Unidades Hidráulicas',
    ],
    icon: 'ri-water-flash-line',
  },
  {
    title: 'Mecânicos',
    items: [
      'Redutores Industriais',
      'Componentes Rotativos',
      'Sistemas de Transmissão',
    ],
    icon: 'ri-settings-6-line',
  },
  {
    title: 'Equipamentos Especiais',
    items: [
      { name: 'Gruas Florestais', link: '/recuperacao-gruas-e-equipamentos-florestais' },
      { name: 'Equipamentos para Papel e Celulose', link: '/recuperacao-equipamentos-papel-celulose' },
      'Equipamentos de Movimentação de Cargas',
      'Equipamentos Industriais de Grande Porte',
    ],
    icon: 'ri-truck-line',
  },
];

export default function Oficina() {
  useSeo({
    title: 'Recuperação de Equipamentos Industriais | Oficina Especializada | Pilar',
    description: 'Recuperação de cilindros hidráulicos, bombas, motores, redutores e equipamentos industriais. Centro de recuperação com diagnóstico técnico, bancada de testes e gestão de serviços especializados.',
    keywords: 'recuperação de equipamentos industriais, recuperação de cilindros hidráulicos, recuperação de bombas hidráulicas, recuperação de motores hidráulicos, recuperação de redutores, centro de recuperação industrial, manutenção hidráulica, Grupo Pilar, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/oficina',
    ogUrl: 'https://www.pilarmanutencao.com.br/oficina',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/OFICINA/HERO/HERO-OFICINA.webp"
              alt="Centro de recuperação de equipamentos industriais do Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
                  Oficina Especializada
                </span>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Recuperação</span> de Equipamentos Industriais
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Recuperamos equipamentos críticos com foco em confiabilidade operacional, redução de paradas e aumento da disponibilidade dos ativos industriais.
              </p>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-3xl">
              <div>
                <div className="flex items-center gap-3 mb-8">
                  <div className="w-8 h-[2px] bg-gp-yellow"></div>
                  <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">O Centro de Recuperação do Grupo Pilar</span>
                </div>
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  Nossa oficina é o centro de recuperação do Grupo Pilar. Com estrutura moderna e protocolos rigorosos de qualidade, realizamos desde recuperações simples até projetos complexos de equipamentos hidráulicos, mecânicos e rotativos de alta criticidade.
                </p>
                <p className="text-gray-400 leading-relaxed mb-6">
                  O processo de recuperação segue etapas técnicas definidas: diagnóstico especializado, desmontagem técnica, recuperação de componentes, montagem precisa e testes em bancadas específicas para garantir a confiabilidade e a segurança do equipamento antes do retorno à operação.
                </p>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <span className="font-display text-3xl font-bold text-gp-yellow">18+</span>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">Anos de Experiência</p>
                  </div>
                  <div className="w-[1px] h-12 bg-gray-700"></div>
                  <div className="text-center">
                    <span className="font-display text-3xl font-bold text-gp-yellow">Todas</span>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">Marcas Atendidas</p>
                  </div>
                  <div className="w-[1px] h-12 bg-gray-700"></div>
                  <div className="text-center">
                    <span className="font-display text-3xl font-bold text-gp-yellow">100%</span>
                    <p className="text-gray-500 text-xs uppercase tracking-wider mt-1">Diagnóstico Técnico Especializado</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                Serviços da Oficina
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {workshopFeatures.map((feature) => (
                <Link
                  key={feature.id}
                  to={feature.id === '1' ? '/recuperacao-cilindros-hidraulicos' : feature.id === '2' ? '/recuperacao-bombas-hidraulicas' : feature.id === '3' ? '/recuperacao-motores-hidraulicos' : feature.id === '4' ? '/recuperacao-redutores-industriais' : '/oficina'}
                  className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20 block"
                >
                  <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <i className={`${feature.icon} text-gp-yellow group-hover:text-gp-graphite w-6 h-6 flex items-center justify-center transition-colors duration-300`}></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {feature.desc}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Capabilities */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                Capacidades Técnicas
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {capabilities.map((cap, index) => (
                <div
                  key={index}
                  className="group bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <i className={`${cap.icon} text-gp-yellow group-hover:text-gp-graphite w-6 h-6 flex items-center justify-center transition-colors duration-300`}></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {cap.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {cap.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Integrated Services */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                Serviços Especializados Integrados ao Processo
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-gray-300 text-lg leading-relaxed mb-6">
                  A Pilar gerencia integralmente a recuperação dos equipamentos, incluindo serviços especializados executados por parceiros homologados e acompanhados tecnicamente.
                </p>
                <p className="text-gray-400 leading-relaxed">
                  Nosso cliente possui um único ponto de responsabilidade, recebendo o equipamento pronto para retorno à operação. Coordenamos cada etapa do processo, desde o diagnóstico até a entrega final validada em bancada.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {integratedServices.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 bg-gp-graphite border border-white/10 rounded-lg px-4 py-3 hover:border-gp-yellow/30 transition-colors duration-300"
                  >
                    <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{service}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Equipment Categories */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                Equipamentos Atendidos
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {equipmentCategories.map((category, index) => (
                <div
                  key={index}
                  className="group bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <i className={`${category.icon} text-gp-yellow group-hover:text-gp-graphite w-6 h-6 flex items-center justify-center transition-colors duration-300`}></i>
                  </div>
                  <h3 className="font-display text-lg font-bold text-white uppercase mb-4 group-hover:text-gp-yellow transition-colors duration-300">
                    {category.title}
                  </h3>
                  <ul className="space-y-2">
                    {category.items.map((item, itemIndex) => (
                      <li key={itemIndex} className="flex items-center gap-2 text-gray-400 text-sm">
                        <div className="w-1.5 h-1.5 bg-gp-yellow rounded-full flex-shrink-0"></div>
                        {typeof item === 'string' ? (
                          item
                        ) : (
                          <Link
                            to={item.link}
                            className="hover:text-gp-yellow transition-colors duration-300"
                          >
                            {item.name}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa recuperar equipamentos industriais com confiabilidade?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Envie seu equipamento para nosso centro de recuperação e receba um diagnóstico técnico completo com proposta de recuperação e prazo de execução.
                </p>
                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar orçamento de recuperação</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}